import React from "react";
import { Link } from "react-router-dom";
import { LongPage, TocEntry } from "./SideNav";
import {
  Bullet,
  Bullets,
  DefItem,
  DefList,
  Ext,
  Mail,
  Note,
  PageHeader,
  Pull,
  Section,
  SimpleTable,
  SubHeading,
} from "./ui";
import { CONTACT_EMAIL, LINKS } from "./constants";

/*
 * NOTE FOR MAINTAINERS
 * --------------------
 * This is the public rendering of SECURITY.md — the document written for the
 * external security reviewers and the internal information security officer.
 * The section ids match that document's structure, so the two can be kept in
 * step: when a control changes there, change it here.
 *
 * Deliberately NOT published here, though present in SECURITY.md: source file
 * and function names, secret-store path layouts, host paths, the development
 * environment's fixed credentials, review scheduling, and internal process.
 * Those are an operational map, not an assurance, and the reviewers already
 * have them. Keep this page technical and keep it short — every claim here
 * must remain true; if a control is removed, remove the claim in the same
 * commit.
 */

const TOC: TocEntry[] = [
  { id: "approach", label: "The approach" },
  { id: "pseudonymization", label: "Pseudonyms and keys" },
  { id: "storage", label: "Stored data" },
  { id: "accounts", label: "Accounts and sessions" },
  { id: "access-control", label: "Access control" },
  { id: "detection", label: "Detecting personal data" },
  { id: "audit-trail", label: "Audit trail" },
  { id: "operations", label: "Infrastructure" },
  { id: "review", label: "Known limitations" },
];

/** The four layers a request passes through, as a plain vertical figure. */
const TopologyFigure: React.FC = () => {
  const layers: Array<{ label: string; detail: string; accent?: boolean }> = [
    {
      label: "The public internet",
      detail: "Reaches one port, over TLS. Everything below is behind a firewall.",
    },
    {
      label: "Web server",
      detail:
        "Terminates TLS 1.2/1.3 with forward-secret cipher suites, sets the transport and framing headers, and hands the request inward over loopback.",
    },
    {
      label: "The application",
      detail:
        "A single service holding both the API and the interface. Runs as an unprivileged user with no capabilities, a read-only filesystem, and a private temporary directory.",
    },
    {
      label: "Database and key store",
      detail:
        "Both bound to loopback only. No route exists that would let a web request reach the key store.",
      accent: true,
    },
  ];

  return (
    <figure className="my-7 rounded-2xl border border-sand-200 bg-white p-5 sm:p-6">
      <ol className="space-y-3">
        {layers.map((layer, i) => (
          <li key={layer.label}>
            <div
              className={`rounded-xl border p-4 ${
                layer.accent
                  ? "border-accent/25 bg-accent-pale/50"
                  : "border-sand-200 bg-sand-50"
              }`}
            >
              <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
                {layer.label}
              </p>
              <p className="mt-2 text-[0.9375rem] leading-relaxed text-[#3B4A48]">{layer.detail}</p>
            </div>
            {i < layers.length - 1 && (
              <div className="flex justify-center py-1 text-sand-300" aria-hidden="true">
                <svg width="14" height="20" viewBox="0 0 14 20" fill="none">
                  <path d="M7 0v14" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2 13l5 6 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
            )}
          </li>
        ))}
      </ol>
      <figcaption className="mt-5 border-t border-sand-200 pt-4 text-sm leading-relaxed text-ink-600">
        Each department runs its own instance. There is no shared multi-tenant store in which one
        department&rsquo;s data sits beside another&rsquo;s.
      </figcaption>
    </figure>
  );
};

/**
 * Worked example for §02: one subject whose kennitala is written two ways —
 * bare in its own column, and inside a clinical note with the "kt." cue —
 * ending up under one pseudonym. The code shown is abbreviated; the real
 * output is a full 44-character base64 string.
 */
const RedactionExample: React.FC = () => {
  const CODE = "Vv9xK2mN7pQ…iJkLmNo=";
  const rows: Array<{ label: string; id: React.ReactNode; note: React.ReactNode }> = [
    {
      label: "As uploaded",
      id: "1234567890",
      note: "Kom í eftirlit 3. mars. Sjá eldri nótu, kt. 123456-7890.",
    },
    {
      label: "As stored and released",
      id: CODE,
      note: `Kom í eftirlit 3. mars. Sjá eldri nótu, kt. ${CODE}.`,
    },
  ];

  return (
    <figure className="my-7 overflow-hidden rounded-xl border border-sand-200 bg-white">
      {rows.map((row, i) => (
        <div key={row.label} className={i > 0 ? "border-t border-sand-200 bg-accent-pale/30" : ""}>
          <p className="px-5 pt-4 text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-ink-600">
            {row.label}
          </p>
          <dl className="grid gap-x-6 gap-y-1 px-5 pb-4 pt-3 sm:grid-cols-[7rem,1fr]">
            <dt className="text-sm font-medium text-ink-600">kennitala</dt>
            <dd className="break-all font-mono text-[0.8125rem] text-ink">{row.id}</dd>
            <dt className="mt-2 text-sm font-medium text-ink-600 sm:mt-1">athugasemd</dt>
            <dd className="break-words font-mono text-[0.8125rem] leading-relaxed text-ink sm:mt-1">
              {row.note}
            </dd>
          </dl>
        </div>
      ))}
      <figcaption className="border-t border-sand-200 bg-sand-50 px-5 py-3 text-sm leading-relaxed text-ink-600">
        Both spellings of the kennitala resolve to one code, so the note still refers to the same
        subject as the column beside it. Codes are abbreviated here for legibility.
      </figcaption>
    </figure>
  );
};

export const SecurityPage: React.FC = () => (
  <LongPage
    toc={TOC}
    intro={
      <PageHeader
        kicker="Security"
        title="How the data is protected"
        lead={
          <p>
            Anyone asked to entrust patient data to a system is entitled to know how it is
            protected, in enough detail to judge for themselves. This page is that account, written
            for people who intend to check it — including the limitations, in §09.
          </p>
        }
      />
    }
  >
    <div className="divide-y divide-sand-200 [&>section:not(:first-child)]:pt-14 [&>section]:pb-14 [&>section:last-child]:pb-0">
      <Section id="approach" number="01" title="The approach">
        <p>
          The design assumption is that a single control will eventually fail, so no single control
          is load-bearing. Identifiers are removed at the point of entry rather than guarded in
          place; the keys that could reverse that live in a separate key-management service; the
          permission decision is made in one place and re-checked wherever data leaves the system;
          and every access is recorded.
        </p>
        <p>
          Two decisions do most of the work. <strong>Project-scoped keys</strong>: a dataset
          pseudonymized for one project produces codes that are useless in any other, so a mistake
          in one project&rsquo;s permissions cannot become a cross-project re-identification.{" "}
          <strong>Visibility and access are separate powers</strong>: seeing that a dataset exists
          and being able to read its contents are distinct permissions, granted independently, so a
          broad view of the catalogue never carries read access with it.
        </p>

        <SubHeading>Who the controls are aimed at</SubHeading>
        <SimpleTable
          head={["Adversary", "What stands in the way"]}
          rows={[
            [
              "Someone outside with no account",
              "Invitation-only registration, a mandatory second factor on every account, rate limits on two independent budgets, parameterized database access, and only the TLS port reachable at all.",
            ],
            [
              "A logged-in researcher reaching past their grant",
              "One canonical authorization function per resource, re-run wherever bytes leave the system; per-project keys, so data from another project would not correlate with their own anyway; and an audit record of the attempt.",
            ],
            [
              "An account with broad visibility of the catalogue",
              "Catalogue visibility and read access are separate grants. Seeing that a dataset exists carries no ability to read it; the read is decided by its own check, re-run on every download path.",
            ],
            [
              "Someone who obtains a copy of the database",
              "Identifier values are encrypted under keys held outside the database; dataset files are not in it at all; passwords are bcrypt-hashed; and every bearer token — session, refresh, invitation, reset — is stored only as a hash.",
            ],
            [
              "Someone who obtains the files on the server",
              "Dataset files are encrypted before the first write, each under its own key held in the key store. An attacker who also obtains the key-store token defeats this — the gap named in §09.",
            ],
            [
              "A compromised software dependency",
              "Dependency install scripts are disabled, the interface container is air-gapped with no route out and no capabilities, and the build installs exactly what the lockfile pins.",
            ],
            [
              "The people who operate and develop the system",
              "Not mitigated. A known design gap; see §09.",
            ],
          ]}
        />
      </Section>

      <Section id="pseudonymization" number="02" title="Pseudonyms and key management">
        <p>
          A pseudonym is produced by keyed hashing — <strong>HMAC-SHA-256</strong>, over a
          canonical form of the value, under a 256-bit key belonging to one project, written out as
          a 44-character URL-safe base64 string. Standard library primitives only: no custom
          algorithms or modes, and all key material and nonces come from the operating
          system&rsquo;s cryptographic random source.
        </p>
        <p>
          Because the key differs per project and only ever travels from the key store into process
          memory, a subject is stably identifiable <em>within</em> a project — joins work — and
          unlinkable <em>across</em> projects. Correlating two projects would require both project
          keys, not one leaked file.
        </p>

        <Bullets>
          <Bullet>
            Keys live in <Ext href={LINKS.vault}>HashiCorp Vault</Ext> and are never written to
            configuration files, environment variables, or local storage.
          </Bullet>
          <Bullet>
            The key that <em>derives</em> pseudonyms and the key that <em>encrypts</em> stored
            original values are different keys. Recovering one does not yield the other.
          </Bullet>
          <Bullet>
            A project&rsquo;s key path is resolved from the project record, never from anything the
            caller supplies, so no request can address another department&rsquo;s key.
          </Bullet>
          <Bullet>
            Re-identification is possible by design — a documented clinical or legal need is a real
            requirement — but there is <strong>no web endpoint that reverses a pseudonym</strong>.
            Reversal exists only as an administrative command on the server, requiring host access,
            the key-store token, and the database together.
          </Bullet>
        </Bullets>

        <SubHeading>The same person gets the same code, however the value was written</SubHeading>
        <p>
          Before hashing, values are canonicalized: separators stripped from kennitölur, email
          addresses lowercased and trimmed, phone numbers reduced to a country-code form. Hashing is
          exact — one character of difference produces an entirely unrelated code — so without this
          step <code>123456-7890</code> and <code>1234567890</code> would become two unrelated
          pseudonyms for one person, and nothing downstream could tell they were the same subject.
        </p>
        <p>
          This matters most where an identifier appears inside free text. A doctor's note column is
          not pseudonymized wholesale - the identifier found <em>within</em> the sentence is replaced.
          Canonicalization is what lets that replacement reuse the code the subject already has in
          the dedicated column:
        </p>

        <RedactionExample />

        <p>
          The security consequence is indirect but real: a researcher whose data no longer joins
          will go looking for the raw identifier — asking for an unredacted export, keeping a
          private copy, or skipping the redaction step altogether. A protective measure that breaks
          the work it sits in front of is one people route around. Keeping the dataset internally
          consistent is what makes redaction something they can accept rather than circumvent.
        </p>

        <Note tone="caution" title="Known limitation: no per-project key rotation">
          <p>
            The master key can be rotated. The per-project pseudonymization keys cannot: rotating
            one would invalidate every pseudonym already issued for that project without a
            re-derivation plan for the whole mapping table, and no such plan exists. Any claim of
            &ldquo;automatic key rotation&rdquo; applies to the master key only.
          </p>
        </Note>
      </Section>

      <Section id="storage" number="03" title="Stored data, and getting rid of it">
        <p>
          Everything sensitive is encrypted with <Ext href={LINKS.aesGcm}>AES-256-GCM</Ext> —
          authenticated encryption, so tampering with a stored value is detected rather than
          silently decrypted into something else.
        </p>

        <DefList>
          <DefItem term="Identifier mappings">
            Original values are encrypted before insertion, under a key distinct from the
            pseudonymization keys. Each mapping records how often and when it was accessed.
          </DefItem>
          <DefItem term="Dataset files">
            Never written to disk in plaintext. Each dataset is encrypted under{" "}
            <strong>its own key</strong>, and the encryption binds the department and dataset
            identity into the ciphertext — so a file moved to another dataset or department will
            not decrypt. Files are written with restrictive permissions, and every path is
            validated against directory traversal.
          </DefItem>
          <DefItem term="Second-factor secrets">
            Encrypted at rest under a separate key again.
          </DefItem>
          <DefItem term="Catalogue metadata">
            <strong>Contains no personal data at all.</strong> When summary statistics are computed,
            identifier columns are skipped outright: no values sampled, no statistics computed, no
            category lists stored. A sensitive column appears as a name, a type, and a flag.
          </DefItem>
        </DefList>

        <SubHeading>Retention, and what deletion actually means</SubHeading>
        <p>
          A dataset can carry an expiry date. The moment it passes, the dataset drops out of
          catalogue listings <em>and</em> is denied by the authorization check itself, before any
          permission rule is consulted.
        </p>
        <p>
          Destruction follows on a scheduled sweep: the encrypted file is unlinked and{" "}
          <strong>that dataset&rsquo;s key is destroyed in the key store</strong>, every version of
          it. Destroying the key is what makes the data unrecoverable rather than merely deleted —
          a filesystem backup taken while the ciphertext existed decrypts to nothing afterwards.
          Three choices shape the sweep:
        </p>
        <Bullets>
          <Bullet>
            <strong>The record survives the data.</strong> The catalogue row is kept, marked as
            purged: who read that dataset, and the fact and time of its destruction, is evidence an
            ethics board may need.
          </Bullet>
          <Bullet>
            <strong>A grace period delays destruction.</strong> Purging is irreversible and a
            mistyped expiry date should be correctable. The dataset is already unreachable
            throughout that window, so the delay costs no confidentiality.
          </Bullet>
          <Bullet>
            <strong>Partial failure is not recorded as success.</strong> A dataset is marked
            destroyed only once both the file and the key are gone; anything short of that is
            retried on the next sweep.
          </Bullet>
        </Bullets>
        <p>
          The same sweep clears expired password-reset tokens, aged password history, and expired
          or revoked refresh tokens.
        </p>
        <Note tone="neutral" title="What retention does not reach">
          <p>
            Destroying a dataset does not destroy the project&rsquo;s pseudonym mapping. That table
            is scoped to the project, not to any dataset, and is kept deliberately so that
            authorized re-identification of a file already in a researcher&rsquo;s hands remains
            possible. Plainly: <strong>expiry destroys the dataset, not the encrypted identifiers
            behind it</strong>. Those persist, under a separate key, for the life of the project;
            reversing them still requires administrative access and is still logged. Deleting a
            project removes its mappings.
          </p>
        </Note>
      </Section>

      <Section id="accounts" number="04" title="Accounts, authentication, and sessions">
        <SubHeading>Getting an account</SubHeading>
        <Bullets>
          <Bullet>
            <strong>Registration is by invitation only.</strong> An invitation is bound to one email
            address; presenting it with a different address is refused. An institutional
            email-domain allowlist is enforced on top of that.
          </Bullet>
          <Bullet>
            Because invitation tokens are stored only as hashes, <strong>an invite link is shown
            exactly once</strong> and cannot be recovered afterwards. A lost link is revoked and
            re-issued rather than looked up.
          </Bullet>
        </Bullets>

        <SubHeading>The second factor is not optional</SubHeading>
        <p>
          Two-factor authentication is mandatory for every account, using standard{" "}
          <Ext href={LINKS.totpRfc}>TOTP</Ext>, and enforced structurally rather than by policy: an
          account without a configured second factor can reach the endpoints that set one up and
          nothing else. No opt-out, no grace period.
        </p>
        <p>
          <strong>Codes are single-use.</strong> A six-digit code is normally valid for its whole
          acceptance window, so one glimpsed over a shoulder or captured from a replayed request
          stays usable for a minute or more. Here, verification identifies which time step a code
          belongs to and consumes it, with the update that records this acting as an atomic gate —
          two requests carrying the same code cannot both succeed, however they are timed.
        </p>

        <SubHeading>Sessions</SubHeading>
        <Bullets>
          <Bullet>
            Sessions use <strong>opaque server-side tokens, not JWTs</strong>. No signing key, no
            self-describing token in circulation; every request is validated by a database lookup
            against a stored hash.
          </Bullet>
          <Bullet>
            That lookup requires the account to still be active, so{" "}
            <strong>disabling an account kills its live sessions immediately</strong> — there is no
            window in which an already-issued signed token remains honoured.
          </Bullet>
          <Bullet>
            Refresh tokens are single-use, rotated on every refresh, and each records its parent,
            forming a family. <strong>Presenting a spent refresh token is treated as evidence of
            compromise</strong>: the request fails and the whole family is revoked, so a stolen
            token cannot be used quietly alongside the legitimate one.
          </Bullet>
          <Bullet>
            Session expiry is an <strong>idle timer</strong> advanced by each authenticated request,
            not a fixed lifetime, so sessions do not die in the middle of an upload. The accepted
            trade-off: a stolen token stays live for as long as it keeps being used.
          </Bullet>
        </Bullets>

        <SubHeading>Passwords</SubHeading>
        <p>
          Hashed with bcrypt at a cost factor above the common default; an existing weaker hash is
          upgraded at the one moment the plaintext is available, the owner&rsquo;s next successful
          login. New passwords are checked against{" "}
          <Ext href={LINKS.pwnedPasswords}>Pwned Passwords</Ext> using its k-anonymity protocol, so
          only a five-character hash prefix leaves the server. The last five passwords cannot be
          reused, and changing a password requires the current password <em>and</em> a valid
          second-factor code. Reset tokens are short-lived, single-use, rate-limited per address,
          and stored as hashes like every other bearer secret.
        </p>

        <SubHeading>Rate limiting</SubHeading>
        <p>
          Limits are configurable per installation; these are the production defaults. Exceeding one
          returns a 429 with a <code>Retry-After</code> header, and the violation is written to the
          audit log.
        </p>
        <SimpleTable
          head={["What", "Default limit"]}
          rows={[
            ["Login attempts, including failed second factors", "5 per 5 minutes, per source address"],
            ["Login attempts against one account", "10 per 15 minutes, per email address"],
            ["Registration", "3 per hour, per source address"],
            ["Session refresh", "10 per 5 minutes, per source address"],
            ["Creating a data request", "10 per hour, per user"],
            ["Administrative writes", "100 per hour, per user"],
            ["Everything else", "60 per minute"],
          ]}
        />
        <p>
          A failed login consumes <strong>both</strong> budgets; a successful one clears both.
          Neither one machine working through many accounts nor many machines working on one account
          runs unconstrained.
        </p>
        <Note tone="neutral">
          <p>
            The per-account budget is deliberately looser than the per-address one, and it is a
            brake rather than a lockout. Anyone who knows an email address can consume it, so an
            indefinite lockout would hand a stranger a denial of service against a named colleague.
            The accepted cost is that a third party can make one account wait.
          </p>
        </Note>
      </Section>

      <Section id="access-control" number="05" title="Access control">
        <p>
          This part of the system is governed by a written doctrine, adopted after a run of
          permission bugs that all had the same shape: the database filter, the list enrichment, the
          download handler, and the interface each carried their own copy of the rules, and the
          copies drifted apart.
        </p>

        <Bullets>
          <Bullet>
            <strong>One canonical function per resource.</strong> Whether a dataset may be
            downloaded is decided in exactly one place; every other layer consumes that answer
            instead of re-deriving it. A new rule is added there or not at all.
          </Bullet>
          <Bullet>
            <strong>Two orthogonal scopes, never conflated.</strong> One setting governs who can see
            that a dataset <em>exists</em>, a separate one who can read its <em>bytes</em>.
            Catalogue filtering touches only the first, permission checks only the second.
            Visible-but-not-downloadable and hidden-but-grantable are both legitimate states. Expiry
            is a third axis that overrides both.
          </Bullet>
          <Bullet>
            <strong>Defence in depth at every exit.</strong> Every endpoint returning sensitive bytes
            re-runs the canonical check itself and never trusts a flag from the request or a previous
            response. A bulk download fails closed if any single dataset in the batch is not
            permitted.
          </Bullet>
          <Bullet>
            <strong>The interface is a thin client.</strong> It renders the permission flags the
            server computes and never derives permissions from the user&rsquo;s role or any other
            client-side hint — which is why you will not see a button here that produces a refusal
            when you press it.
          </Bullet>
          <Bullet>
            <strong>The access matrix is pinned by tests</strong> on three axes: is the resource
            listed, what does the permission flag say, and does the endpoint actually succeed. Every
            meaningful combination of scope, role, and relationship has an explicit test, so when
            the rules change the matrix is what breaks first.
          </Bullet>
        </Bullets>

        <SubHeading>Roles</SubHeading>
        <p>
          There are two independent axes: a role in the department and a role on each project. Four
          constraints matter more than the rest.
        </p>
        <DefList>
          <DefItem term="Visibility never implies access">
            A role can be granted a wide view of the catalogue without being granted the ability to
            read anything in it. Downloads are settled solely by the canonical check, re-run at
            every exit, so a visibility grant cannot widen into a read.
          </DefItem>
          <DefItem term="Escalation is constrained">
            Promotion to principal investigator is restricted to department-level administration; a
            PI without that standing may assign roles only on their own project; no role change
            crosses a department boundary; and removing a project&rsquo;s last PI is refused. Every
            role change is audited.
          </DefItem>
          <DefItem term="The command line is not a bypass">
            The administrative commands that mint or grant privileged roles require root access on
            the server itself.
          </DefItem>
          <DefItem term="Data providers see almost nothing">
            Hospital and registry accounts are confined to the upload endpoints: no catalogue, no
            projects, no results, no other department&rsquo;s requests.
          </DefItem>
        </DefList>
      </Section>

      <Section id="detection" number="06" title="Detecting personal data in uploads">
        <p>Uploads are inspected before anything is stored, in two layers.</p>
        <Bullets>
          <Bullet>
            <strong>Column level.</strong> Columns are classified by name and by content. A
            checksum-valid kennitala, a well-formed email address, a formatted phone number, or an
            Icelandic patronymic is high-confidence evidence on a single value; for weaker signals
            such as personal names, a configurable majority of matching values is required before
            the column is flagged.
          </Bullet>
          <Bullet>
            <strong>Free text.</strong> The harder case, and the one people miss by hand: an
            identifier sitting inside a sentence in a clinical note. Non-identifier columns are
            sampled and searched for embedded identifiers, and each finding is returned with its
            surrounding text so a human sees the context rather than a bare match.
          </Bullet>
        </Bullets>
        <p>
          The uploader then decides, finding by finding: replace the matched text with the
          project&rsquo;s real pseudonym for that subject, or drop the column so its raw contents
          are never stored. Both happen <strong>before</strong> encryption and storage — an
          unreviewed raw identifier does not reach the store and then get cleaned up afterwards.
        </p>
        <Note tone="caution" title="Detection assists a human decision; it does not replace one">
          <p>
            This is heuristic matching. It substantially reduces accidental disclosure and it is not
            a guarantee — which is why a person confirms every finding and the review screen shows
            each one in context. It must not be represented to a data subject or an ethics board as
            a completeness guarantee; responsibility for what is confirmed stays with the uploader.
          </p>
        </Note>
      </Section>

      <Section id="audit-trail" number="07" title="Audit trail">
        <p>
          Recorded: successful and failed logins with the reason, logouts, logins refused for lack
          of a department, logins from a location not seen before, rate-limit violations and
          throttled logins, files processed, dataset downloads together with the columns accessed,
          data request submissions and decisions, and every role change. Each entry carries the
          user, the project, the resource, the source address, the user agent, and a timestamp.
        </p>

        <Pull>The audit tables refuse to be edited — and the refusal is in the database, not the application.</Pull>

        <p>
          A database trigger raises an error on any attempt to update, delete, or truncate a row in
          the audit tables. The account the application uses can append to history and read it; it
          cannot rewrite it, and neither can anyone connecting with those same credentials directly.
        </p>
        <p>
          The user and dataset references in those tables are deliberately <em>not</em> foreign
          keys. A foreign key with a cascade or a null-on-delete would mean that deleting a user or
          a dataset silently edits the record of what they did — the exact property an audit trail
          exists to prevent.
        </p>
        <p>
          In ordinary operational logs, pseudonyms are truncated and bearer tokens are never
          written, so day-to-day log files carry no full identifiers and nothing replayable.
        </p>
      </Section>

      <Section id="operations" number="08" title="Infrastructure, deployment, and supply chain">
        <TopologyFigure />

        <SubHeading>At the edge</SubHeading>
        <p>
          TLS 1.2/1.3 with forward-secret, authenticated cipher suites and certificates from a
          public certificate authority. Strict transport security with a long lifetime and
          preloading; framing denied, content-type sniffing disabled, referrer information
          restricted, browsing contexts isolated.
        </p>
        <p>
          A <strong>content security policy</strong> confines scripts to the application&rsquo;s own
          origin with no inline script permitted — which holds because the production build emits
          none — and denies framing, plugin content, and base-URI rewriting. Cross-origin requests{" "}
          <strong>fail closed</strong>: permission headers are emitted only for an origin on an
          explicit allowlist, and a misconfigured or empty allowlist grants nothing rather than
          everything. These headers are set by the application itself as well as by the web server,
          so they survive a reverse-proxy misconfiguration instead of disappearing with it.
        </p>

        <SubHeading>Trusting the address a request came from</SubHeading>
        <p>
          Rate limits, the audit trail, and new-location detection all key on the client address,
          which makes address resolution a security control rather than a convenience. Forwarding
          headers are ignored unless the immediate peer is a configured trusted proxy, and when
          honoured the header is walked from the end the proxy actually wrote — a client can prepend
          anything it likes to the other end. An empty allowlist believes no header at all.
        </p>

        <SubHeading>Configuration and secrets</SubHeading>
        <Bullets>
          <Bullet>
            All secrets — database credentials, encryption keys, per-project and per-dataset keys —
            live in the key store. Configuration files hold no secret values; the environment
            supplies only the key store&rsquo;s address and token.
          </Bullet>
          <Bullet>
            <strong>An unrecognized configuration key is a startup failure.</strong> A setting that
            looks like a control but silently does nothing is worse than a missing one. Disabling
            mail-server certificate verification is refused outright, as is a plaintext database
            connection to anything but the local host.
          </Bullet>
          <Bullet>
            Upload size is bounded by streaming limits on the request body itself rather than a
            buffer size that only looks like a limit; oversized uploads are rejected rather than
            spilled to disk. Uploads are restricted to CSV, and delimiter detection is centralized
            so a semicolon-delimited file cannot collapse into a single column and slip past
            column-level inspection.
          </Bullet>
          <Bullet>
            Rich-text content in administrative announcements is sanitized server-side against an
            allowlist before storage and again before it is embedded in email. The editor in the
            browser matches that policy, but the server is the authority.
          </Bullet>
        </Bullets>

        <SubHeading>Supply chain</SubHeading>
        <p>
          Backend dependencies are few, mainstream, and checksum-verified, and the service is built
          as a static binary. On the interface side, the main execution vector is closed:{" "}
          <strong>dependency lifecycle scripts never run</strong>, and the build installs exactly
          what the lockfile pins, failing loudly if the two have drifted. In development, the
          container running third-party JavaScript sits on a network with no gateway, drops all
          capabilities, runs as a non-root user, and publishes no port; a minimal sidecar pinned by
          image digest, running no package code, is the only thing that publishes a port.
        </p>
        <p>Every push runs a security stage that gates the build:</p>
        <SimpleTable
          head={["Check", "What it catches"]}
          rows={[
            [
              "Reachable-vulnerability analysis",
              "Known vulnerabilities in dependencies and in the language's own standard library — call-graph aware, so it reports what the code can actually reach.",
            ],
            ["Dependency advisories", "Published advisories in the interface's production dependencies."],
            ["Static analysis", "Correctness defects across the whole codebase."],
            [
              "Query construction audit",
              "Any database query built by string construction rather than bound parameters.",
            ],
            [
              "Software bill of materials",
              "The exact dependency set of every build, retained — so when a vulnerability is published later, which builds contained it is a lookup rather than an investigation.",
            ],
          ]}
        />
        <p>
          Unit tests cover the security-relevant paths specifically — the access matrix, session
          expiry and extension, rate limiting, password policy, address resolution, second-factor
          replay, personal-data detection, and the cryptographic code — and a containerized
          integration suite exercises injection, access, expiry, and registration gating end to end.
        </p>
        <p>
          Host provisioning — web server, TLS, firewall, service hardening, and key-store
          configuration — lives in a separate infrastructure repository.
        </p>
      </Section>

      <Section id="review" number="09" title="Known limitations, and reporting a problem">
        <p>
          Processing of personal data in this system is subject to the{" "}
          <Ext href={LINKS.gdpr}>GDPR</Ext> and, in Iceland, to the supervision of{" "}
          <Ext href={LINKS.personuvernd}>Persónuvernd</Ext>. Pseudonymization is one of the
          technical measures those rules expect; it does not by itself discharge a controller of any
          other obligation.
        </p>
        <p>These are the gaps we know about.</p>

        <DefList>
          <DefItem term="Operators are trusted">
            Someone with server access, the key-store token, and the database can decrypt any
            mapping and any dataset. The intent is that operators should not be able to read
            research data at all, and <strong>no technical control enforces this today</strong> —
            the protections are procedural. It is the most significant open item in the design.
          </DefItem>
          <DefItem term="No rotation for the keys that matter">
            Per-project pseudonymization keys cannot currently be rotated. See §02.
          </DefItem>
          <DefItem term="No second-factor recovery codes">
            The only recovery from a lost authenticator is an administrator disabling the second
            factor, which makes account recovery depend on that administrator verifying the request
            out of band.
          </DefItem>
          <DefItem term="The audit trail is only as durable as the database">
            Append-only is enforced by the database, which leaves two limits: a database superuser
            could remove that enforcement, and the trail is not shipped to write-once storage
            elsewhere, so losing the database means losing the history.
          </DefItem>
          <DefItem term="The key store is a single point">
            One node, no high availability, no automatic unsealing, and a long-lived token on disk.
            Recovery after a reboot is a manual step.
          </DefItem>
          <DefItem term="Mail may not be configured">
            Where outgoing mail is not enabled, invitations and password resets depend on an
            administrator passing them on by hand. New-location logins are recorded but nobody is
            notified.
          </DefItem>
          <DefItem term="The breach check fails open">
            If the password breach service is unreachable, passwords are accepted without that check
            rather than registration being blocked.
          </DefItem>
          <DefItem term="Rate limiting lives in one process">
            Counters reset when the service restarts, and the design does not survive running
            several instances behind a load balancer. A shared counter store is a redesign, not a
            patch.
          </DefItem>
          <DefItem term="Personal-data detection is heuristic">
            See §06. It is an aid to a human decision and is not represented as anything more.
          </DefItem>
        </DefList>

        <p>
          The{" "}
          <Link to="/roadmap" className="text-accent underline underline-offset-2">
            roadmap
          </Link>{" "}
          sets out where the system stands overall.
        </p>

        <SubHeading>If you find a vulnerability</SubHeading>
        <p>
          Write to <Mail address={CONTACT_EMAIL} subject="Security report" />, or tell your
          department&rsquo;s manager and they will route it to the maintainers. Please do not probe
          further than needed to establish that a problem exists, do not access or retain anyone
          else&rsquo;s data, and please do not disclose publicly before it has been addressed.
          Reports are welcome from anyone — a finding you are unsure about is still worth sending.
        </p>
      </Section>
    </div>
  </LongPage>
);
